/**
 * Карточка «Ответственный за экологию» (ecology-followup, ответ заказчика 18.09): человек, который
 * отвечает за сроки и отчётность по экологии. Кабинет шлёт ему письма о сроках раздела вдобавок к
 * почте организации и контактам с галочкой «получает уведомления».
 *
 * Живёт в реквизитах организации (файл заказчика 23.09.2026): у клиента — в «Настройках», у команды —
 * во вкладке «Реквизиты» карточки клиента. Один компонент на обе стороны — тот же эндпоинт по скоупу.
 * Контакт передаёт владелец секции (`EcoRequisites`), сохранение отвечает свежим обзором, как и
 * остальные правки раздела.
 */
import { useEffect, useState, type FormEvent } from 'react'
import type { EcoOverview, EcoResponsible, EcoScopeApi } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { normalizePersonName } from '@/lib/name-case'

export function EcoResponsibleCard({
  api,
  responsible,
  onSaved,
}: {
  api: EcoScopeApi
  responsible: EcoResponsible
  /** Свежий обзор после сохранения — владелец секции обновляет им своё состояние. */
  onSaved: (overview: EcoOverview) => void
}) {
  const [draft, setDraft] = useState<EcoResponsible>(responsible)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDraft(responsible)
  }, [responsible])

  const patch = (next: Partial<EcoResponsible>) => {
    setSaved(false)
    setDraft((prev) => ({ ...prev, ...next }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setError(null)
    setSaving(true)
    try {
      onSaved(await api.saveResponsible(draft))
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить ответственного'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card header="Ответственный за экологию" className="mt-4">
      <form onSubmit={submit} noValidate>
        <p className="text-13 text-txt-secondary">
          Кому писать о сроках раздела «Экология». Письма придут и на почту организации — этот
          контакт добавляется к ней.
        </p>
        {error ? (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        ) : null}
        <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-3">
          <Field label="Фамилия, имя, отчество">
            {(props) => (
              <Input
                {...props}
                value={draft.name}
                placeholder="Петрова Анна Сергеевна"
                onChange={(e) => patch({ name: e.target.value })}
                // Регистр — на уходе с поля (names-and-positions-normalization).
                onBlur={() => patch({ name: normalizePersonName(draft.name) })}
                spellCheck={false}
              />
            )}
          </Field>
          <Field label="Электронная почта" hint="На неё придут письма о сроках">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={draft.email}
                placeholder="eco@example.ru"
                onChange={(e) => patch({ email: e.target.value })}
              />
            )}
          </Field>
          <Field label="Телефон">
            {(props) => (
              <Input
                {...props}
                value={draft.phone}
                placeholder="+7 900 000-00-00"
                onChange={(e) => patch({ phone: e.target.value })}
              />
            )}
          </Field>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
          {saved ? (
            <span role="status" className="text-13 font-medium text-green">
              Сохранено
            </span>
          ) : null}
        </div>
      </form>
    </Card>
  )
}
